import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../lib/api';
import { confirmDialog } from '../../utils/dialogs';

export default function EnrollmentMessagesModal({ onClose, onEnrollmentUpdate }) {
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        fetchEnrollments();
    }, []);

    const fetchEnrollments = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await fetch(`${API_BASE}/api/enrollment`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                },
            });
            if (!response.ok) {
                throw new Error("Failed to fetch enrollments");
            }

            const data = await response.json();
            setEnrollments(data);
        } catch (error) {
            console.error("Error fetching enrollments:", error);
            setError("Failed to load enrollment messages");
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (enrollmentId, newStatus) => {
        try {
            const response = await fetch(`${API_BASE}/api/enrollment/${enrollmentId}/status`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                },
                body: JSON.stringify({ status: newStatus }),
            });

            if (!response.ok) {
                throw new Error("Failed to update status");
            }

            // Refresh the list
            fetchEnrollments();

            // Notify parent component to update unread count
            console.log('📝 Enrollment status updated, calling onEnrollmentUpdate callback...');
            if (onEnrollmentUpdate) {
                console.log('✅ onEnrollmentUpdate callback exists, calling it...');
                onEnrollmentUpdate();
            } else {
                console.log('❌ onEnrollmentUpdate callback is undefined!');
            }
        } catch (error) {
            console.error("Error updating status:", error);
            alert("Failed to update enrollment status");
        }
    };

    const deleteEnrollment = async (enrollmentId) => {
        if (!confirmDialog("Are you sure you want to delete this enrollment?")) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE}/api/enrollment/${enrollmentId}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to delete enrollment");
            }

            setEnrollments(prev => prev.filter(e => e.id !== enrollmentId));
            alert("Enrollment deleted successfully");

            // Notify parent component to update unread count
            console.log('🗑️ Enrollment deleted, calling onEnrollmentUpdate callback...');
            if (onEnrollmentUpdate) {
                console.log('✅ onEnrollmentUpdate callback exists, calling it...');
                onEnrollmentUpdate();
            } else {
                console.log('❌ onEnrollmentUpdate callback is undefined!');
            }
        } catch (error) {
            console.error("Error deleting enrollment:", error);
            alert("Failed to delete enrollment");
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'NEW': return 'bg-blue-100 text-blue-800';
            case 'REVIEWING': return 'bg-yellow-100 text-yellow-800';
            case 'APPROVED': return 'bg-green-100 text-green-800';
            case 'REJECTED': return 'bg-red-100 text-red-800';
            case 'CONTACTED': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 pt-20">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[80vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-slate-900">Enrollment Messages</h2>
                        <button type="button" onClick={onClose} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>
                    </div>

                    {error && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                            {error}
                        </div>
                    )}

                    {loading ? (
                        <div className="text-center py-8 text-slate-500">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-slate-400 mr-2"></div>
                            Loading enrollment messages...
                        </div>
                    ) : enrollments.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                            <div className="text-4xl mb-4">📝</div>
                            <p className="text-lg font-medium">No enrollment messages yet</p>
                            <p className="text-sm">Students can submit enrollment requests from the main page</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {enrollments.map((enrollment) => (
                                <div key={enrollment.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-4 mb-2">
                                                <h4 className="font-medium text-slate-900">
                                                    {enrollment.firstName} {enrollment.lastName}
                                                </h4>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(enrollment.status)}`}>
                                                    {enrollment.status}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4 text-sm text-slate-600 mb-3">
                                                <div>
                                                    <span className="font-medium">Phone:</span> {enrollment.phone}
                                                </div>
                                                <div>
                                                    <span className="font-medium">Email:</span> {enrollment.email}
                                                </div>
                                                <div>
                                                    <span className="font-medium">Program:</span> {enrollment.program}
                                                </div>
                                                <div>
                                                    <span className="font-medium">Submitted:</span> {new Date(enrollment.submittedAt).toLocaleDateString()}
                                                </div>
                                            </div>

                                            {enrollment.message && (
                                                <div className="bg-slate-50 p-3 rounded border-l-4 border-blue-400">
                                                    <p className="text-sm text-slate-700">{enrollment.message}</p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex flex-col space-y-2 ml-4">
                                            <select
                                                value={enrollment.status}
                                                onChange={(e) => updateStatus(enrollment.id, e.target.value)}
                                                className="px-3 py-1 border border-slate-300 rounded text-sm"
                                            >
                                                <option value="NEW">New</option>
                                                <option value="REVIEWING">Reviewing</option>
                                                <option value="APPROVED">Approved</option>
                                                <option value="REJECTED">Rejected</option>
                                                <option value="CONTACTED">Contacted</option>
                                            </select>

                                            <button
                                                onClick={() => deleteEnrollment(enrollment.id)}
                                                className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200 transition-colors"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
